import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Database, Search, Layers, Zap, BarChart3, Loader2 } from 'lucide-react';
import { getPublicTopics, getPublicTopicQA } from '../../lib/apiClient';

const FeatureCard = ({ icon: Icon, title, description }) => (
  <div className="p-6 rounded-2xl bg-surface border border-white/5 hover:border-primary/30 transition-all hover:shadow-glow-blue group">
    <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center mb-4 group-hover:bg-primary/20 transition-colors">
      <Icon className="w-6 h-6 text-blue-400 group-hover:text-primary transition-colors" />
    </div>
    <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
    <p className="text-slate-400 text-sm leading-relaxed">{description}</p>
  </div>
);

const Step = ({ number, title, description }) => (
  <div className="relative flex flex-col items-center text-center max-w-xs mx-auto">
    <div className="w-12 h-12 rounded-full bg-surface border border-white/10 flex items-center justify-center text-xl font-bold text-white mb-4 shadow-glow-purple">
      {number}
    </div>
    <h3 className="text-lg font-bold text-white mb-2">{title}</h3>
    <p className="text-slate-400 text-sm">{description}</p>
  </div>
);

export default function HomePage() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [trendingTopics, setTrendingTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isHoveringCard, setIsHoveringCard] = useState(false);
  const [topicQaBySlug, setTopicQaBySlug] = useState({}); // { [slug]: QAEntry[] }
  const [qaLoadingSlug, setQaLoadingSlug] = useState(null);

  // Typewriter Effect State
  const [placeholder, setPlaceholder] = useState('');
  const [wordIndex, setWordIndex] = useState(0);
  const [charIndex, setCharIndex] = useState(0);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  
  const examples = ['AI Safety', 'Biohacking', 'Crypto Regulation', 'Sustainable Energy', 'Space Exploration'];

  useEffect(() => {
      const currentWord = examples[wordIndex];
      
      // Don't animate if user has typed something
      if (query) return;

      const typeSpeed = isDeleting ? 50 : 100;
      const pauseTime = 2000;

      if (isPaused) {
          const timeout = setTimeout(() => {
              setIsPaused(false);
              setIsDeleting(true);
          }, pauseTime);
          return () => clearTimeout(timeout);
      }

      const timeout = setTimeout(() => {
          if (!isDeleting && charIndex < currentWord.length) {
              // Typing
              setPlaceholder(currentWord.substring(0, charIndex + 1));
              setCharIndex(prev => prev + 1);
          } else if (isDeleting && charIndex > 0) {
              // Deleting
              setPlaceholder(currentWord.substring(0, charIndex - 1));
              setCharIndex(prev => prev - 1);
          } else if (!isDeleting && charIndex === currentWord.length) {
              // Finished word
              setIsPaused(true);
          } else if (isDeleting && charIndex === 0) {
              // Finished deleting
              setIsDeleting(false);
              setWordIndex((prev) => (prev + 1) % examples.length);
          }
      }, typeSpeed);

      return () => clearTimeout(timeout);
  }, [charIndex, isDeleting, isPaused, wordIndex, query]);

  useEffect(() => {
      async function loadTrending() {
          try {
              const data = await getPublicTopics({ sort: 'trending', limit: 5 });
              setTrendingTopics(data || []);
          } catch (e) {
              console.error('Failed to load trending topics', e);
          } finally {
              setLoading(false);
          }
      }
      loadTrending();
  }, []);

  // Helper: load Q&A for a specific topic slug (cached per slug)
  const loadQaForTopic = async (slug) => {
    if (!slug) return;
    if (topicQaBySlug[slug]) return; // already loaded
    if (qaLoadingSlug === slug) return; // already in-flight

    try {
      setQaLoadingSlug(slug);
      const res = await getPublicTopicQA(slug);
      const qa = Array.isArray(res?.data) ? res.data : [];
      setTopicQaBySlug(prev => ({ ...prev, [slug]: qa }));
    } catch (e) {
      console.error('Failed to load topic Q&A for home hero', slug, e);
      setTopicQaBySlug(prev => ({ ...prev, [slug]: [] }));
    } finally {
      setQaLoadingSlug(current => (current === slug ? null : current));
    }
  };

  // Auto-rotate through trending topics
  useEffect(() => {
    if (!trendingTopics || trendingTopics.length <= 1) return;
    if (isHoveringCard) return;

    const interval = setInterval(() => {
      setActiveIndex((prev) => {
        if (!trendingTopics || trendingTopics.length === 0) return 0;
        return (prev + 1) % trendingTopics.length;
      });
    }, 8000); // 8s per topic

    return () => clearInterval(interval);
  }, [trendingTopics, isHoveringCard]);

  // Whenever active topic changes, best-effort load its Q&A
  useEffect(() => {
    if (!trendingTopics || trendingTopics.length === 0) return;
    const clampedIndex = Math.min(activeIndex, trendingTopics.length - 1);
    const topic = trendingTopics[clampedIndex];
    if (!topic || !topic.slug) return;
    loadQaForTopic(topic.slug);
  }, [activeIndex, trendingTopics]);

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      navigate(`/topics?q=${encodeURIComponent(query)}`);
    } else {
        navigate('/topics');
    }
  };

  // Use active trending topic or fallback to AI Safety example
  const activeTrendingTopic = (trendingTopics && trendingTopics.length > 0)
    ? trendingTopics[Math.min(activeIndex, trendingTopics.length - 1)]
    : null;

  const featuredTopic = activeTrendingTopic || {
      title: 'AI Safety & Alignment',
      description: 'The intersection of risk modeling, autonomy, and next-world decision making.',
      summary: 'AI Safety focuses on preventing accidents, misuse, and harmful consequences of artificial intelligence systems.',
      slug: 'ai-safety',
      isMock: true
  };

  const activeSlug = featuredTopic.slug;
  const qaEntries = activeSlug && topicQaBySlug[activeSlug] ? topicQaBySlug[activeSlug] : [];
  const qaLoading = qaLoadingSlug === activeSlug;

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6">
        {/* Background Glows */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[1000px] h-[600px] bg-blue-600/20 rounded-full blur-[120px] -z-10 opacity-50 pointer-events-none" />
        <div className="absolute bottom-0 right-0 w-[800px] h-[600px] bg-purple-600/10 rounded-full blur-[120px] -z-10 opacity-30 pointer-events-none" />

        <div className="container mx-auto max-w-6xl grid lg:grid-cols-2 gap-16 items-center">
          <div className="text-left">
            <h1 className="text-5xl lg:text-7xl font-bold tracking-tight text-white mb-6 leading-tight">
              YOUR PODCAST <br />
              DESERVES TO BE <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-500">IMMORTAL</span>
            </h1>
            <p className="text-xl text-slate-400 mb-8 max-w-lg leading-relaxed">
              Not buried in audio. Not lost in the feed. Most shows don't need more clips – they need the right people to find the right ideas. PodVault turns every episode into structured topic pages designed to rank in AI search and Google, attribute clicks back to the brand, and keep working long after release day.
            </p>
            
            {/* CTAs & Search */}
            <div className="flex flex-col gap-8">
                <div className="flex flex-wrap gap-4">
                    <Link to="/pricing" className="px-8 py-4 bg-gradient-primary text-white font-bold rounded-full shadow-glow-blue hover:scale-105 transition-transform flex items-center gap-2">
                        Make My Podcast Immortal
                        <ArrowRight className="w-5 h-5" />
                    </Link>
                </div>

                <div className="max-w-md">
                    <form onSubmit={handleSearch} className="relative group">
                        <input 
                            type="text"
                            value={query}
                            onChange={(e) => setQuery(e.target.value)}
                            placeholder={query ? '' : `Search ideas (e.g. '${placeholder}')`} 
                            className="w-full bg-surface/50 border border-white/10 rounded-full py-3 pl-12 pr-4 text-white placeholder:text-slate-600 focus:outline-none focus:border-primary/50 focus:ring-1 focus:ring-primary/50 transition-all"
                        />
                        <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500 group-focus-within:text-primary transition-colors" />
                        <button type="submit" className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/5 hover:bg-white/10 text-slate-300 p-1.5 rounded-full transition-colors">
                            <ArrowRight className="w-4 h-4" />
                        </button>
                    </form>
                </div>
            </div>
          </div>

          {/* Hero Visual - Glassmorphic Card (Dynamic) */}
          <div 
            className="relative"
            onMouseEnter={() => setIsHoveringCard(true)}
            onMouseLeave={() => setIsHoveringCard(false)}
          >
            <div className="absolute inset-0 bg-gradient-to-tr from-blue-500/20 to-purple-500/20 rounded-2xl blur-2xl -z-10" />
            <div className="bg-surface/90 backdrop-blur-xl border border-white/10 rounded-2xl p-6 shadow-2xl transform rotate-1 hover:rotate-0 transition-transform duration-500">
              {/* Breadcrumb Mock */}
              <div className="flex items-center gap-2 text-[10px] text-slate-500 mb-4 font-mono">
                 <span>Home</span> &gt; <span>Topics</span> &gt; <span className="text-white">{featuredTopic.title}</span>
              </div>

              {/* Topic Header Area */}
              <div className="mb-6">
                <h3 className="text-2xl font-bold text-white mb-2">{featuredTopic.title}</h3>
                <p className="text-slate-400 text-sm leading-relaxed mb-3 line-clamp-2">
                  {featuredTopic.description}
                </p>
                <div className="flex gap-2">
                  {featuredTopic.isMock ? (
                      <>
                        <span className="px-2 py-0.5 rounded-full bg-white/10 border border-white/10 text-[10px] text-slate-300">Risk Modeling</span>
                        <span className="px-2 py-0.5 rounded-full bg-white/10 border border-white/10 text-[10px] text-slate-300">RLHF</span>
                      </>
                  ) : (
                      <span className="px-2 py-0.5 rounded-full bg-white/10 border border-white/10 text-[10px] text-slate-300">Trending Now</span>
                  )}
                </div>
              </div>

              {/* Explanatory Panel */}
              <div className="bg-black/40 rounded-lg p-4 border border-white/5 mb-6">
                  <div className="text-[10px] font-bold text-primary uppercase tracking-wide mb-2 flex items-center gap-1">
                    <div className="w-1 h-1 rounded-full bg-primary"/> What This Topic Covers
                  </div>
                  {featuredTopic.summary ? (
                      <p className="text-xs text-slate-300 line-clamp-3">{featuredTopic.summary}</p>
                  ) : (
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2 text-xs text-slate-300">
                          <div className="w-1 h-1 rounded-full bg-primary mt-1.5 flex-shrink-0" />
                          Technical alignment (making AI do what we want)
                        </li>
                      </ul>
                  )}
              </div>

              {/* Q&A Spotlight */}
              <div className="bg-black/40 rounded-lg p-4 border border-white/5 mb-6">
                <div className="text-[10px] font-bold text-slate-400 uppercase tracking-wide mb-2 flex items-center gap-1">
                  <div className="w-1 h-1 rounded-full bg-primary" /> Q&A Spotlight
                </div>
                {qaLoading && (
                  <div className="text-[11px] text-slate-500 flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" />
                    Loading questions…
                  </div>
                )}
                {!qaLoading && qaEntries && qaEntries.length > 0 && (
                  <div className="space-y-1.5">
                    <div className="text-[11px] font-semibold text-slate-200 line-clamp-2">
                      Q: {qaEntries[0].question}
                    </div>
                    <div className="text-[11px] text-slate-400 line-clamp-3">
                      A: {qaEntries[0].answer}
                    </div>
                  </div>
                )}
                {!qaLoading && (!qaEntries || qaEntries.length === 0) && (
                  <div className="text-[11px] text-slate-500">
                    Q&A coming soon for this topic.
                  </div>
                )}
              </div>

              {/* Insights Preview */}
              <div className="space-y-3">
                <div className="text-[10px] font-bold text-slate-500 uppercase tracking-wide">Top Insight</div>
                <div className="p-3 rounded-lg bg-surface border border-white/5 hover:border-primary/30 transition-colors group cursor-pointer">
                   <div className="text-sm font-bold text-white group-hover:text-primary transition-colors mb-1">
                       {featuredTopic.recent_insight_count ? `${featuredTopic.recent_insight_count} new insights this month` : 'Why RLHF is a temporary patch'}
                   </div>
                   <div className="flex items-center justify-between text-[10px] text-slate-500">
                      <div className="flex items-center gap-1">
                         <div className="w-3 h-3 rounded bg-white/10"/> 
                         <span>Ep. 14 • Acme Robotics</span>
                      </div>
                   </div>
                </div>
              </div>

              <div className="pt-4 mt-4 border-t border-white/5 text-center">
                  <Link to={`/topics/${featuredTopic.slug}`} className="text-xs font-mono text-blue-400 hover:underline">→ View Topic Page</Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Strip */}
      <section className="py-20 border-y border-white/5 bg-black/20">
        <div className="container mx-auto max-w-6xl px-6">
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <FeatureCard 
              icon={Search}
              title="Tired of ideas buried in audio?"
              description="We pull out the key insights and turn them into skimmable, searchable pages that people can actually find and share."
            />
            <FeatureCard 
              icon={BarChart3}
              title="Tired of zero attribution?"
              description="We build permanent pages that keep driving traffic back to you—even years after you record the episode."
            />
            <FeatureCard 
              icon={Layers}
              title="Tired of generic AI tools?"
              description="We learn how you talk and write, then generate insights that match your voice—not some generic marketing bot."
            />
            <FeatureCard 
              icon={Database}
              title="Tired of lost authority?"
              description="We map your podcast to the topics you own, so search engines and AI know you're the authority."
            />
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-32 px-6">
        <div className="container mx-auto max-w-4xl text-center mb-20">
          <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">YOUR PODCAST, TRANSFORMED INTO A <span className="text-blue-400">TOPIC INTELLIGENCE ENGINE</span></h2>
        </div>

        <div className="container mx-auto max-w-6xl">
            <div className="grid md:grid-cols-3 gap-8 mb-16">
                 <div className="p-8 rounded-2xl bg-surface border border-white/10 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <span className="text-9xl font-bold text-white">1</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">Extract</h3>
                    <p className="text-slate-400">We listen to each episode and identify the core ideas, themes, and insights worth preserving.</p>
                 </div>
                 <div className="p-8 rounded-2xl bg-surface border border-blue-500/30 shadow-glow-blue relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <span className="text-9xl font-bold text-blue-500">2</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">Enrich</h3>
                    <p className="text-slate-400">We turn those ideas into structured topic pages and brand insights that search engines and AI understand.</p>
                 </div>
                 <div className="p-8 rounded-2xl bg-surface border border-white/10 relative overflow-hidden group">
                    <div className="absolute top-0 right-0 p-4 opacity-10 group-hover:opacity-20 transition-opacity">
                        <span className="text-9xl font-bold text-white">3</span>
                    </div>
                    <h3 className="text-2xl font-bold text-white mb-4">Elevate</h3>
                    <p className="text-slate-400">We publish your insights into topic hubs that surface in search results, AI answers, and industry conversations.</p>
                 </div>
            </div>

            {/* Timeline Steps */}
            <div className="flex flex-col md:flex-row justify-between items-start gap-8 border-t border-white/10 pt-16">
                <Step number="1" title="Upload" description="Import your episode RSS feed." />
                <Step number="2" title="Extract" description="PodVault maps the ideas." />
                <Step number="3" title="Build" description="We create permanent topic pages." />
                <Step number="4" title="Voice" description="Insights in your brand voice." />
                <Step number="5" title="Rank" description="AI Search finds you first." />
            </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-32 px-6 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-radial from-blue-900/20 to-transparent pointer-events-none" />
        
        <div className="container mx-auto max-w-3xl relative z-10">
            <h2 className="text-4xl md:text-6xl font-bold text-white mb-8">Pilot Plan</h2>
            <Link
              to="/signup"
              className="relative inline-flex items-center justify-center mb-8 px-9 py-3.5 rounded-full font-semibold text-sm tracking-wide uppercase text-black bg-white shadow-[0_0_35px_rgba(15,23,42,0.75)] hover:bg-slate-50 transition-colors"
            >
              <span className="absolute inset-0 blur-2xl bg-purple-500/70 rounded-full opacity-100 -z-10" />
              <span className="relative flex items-center gap-2">
                Book your immortality call today
              </span>
            </Link>
            <p className="text-xl text-slate-400">
                Stop letting your best ideas disappear into the feed. Start building your Topic Engine today.
            </p>
        </div>
      </section>
    </div>
  );
}
